import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages } from '../data/mock';
import { ArrowRight, Shield, Truck, Zap, Star, ChevronRight, Check, Play, Award, Target, Flame, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';

const HomePage = () => {
  const { addToCart } = useCart();
  const featuredProducts = products.filter(p => p.badge).slice(0, 4);

  useEffect(() => {
    document.title = 'Keto Vybe | Keto Supplements & Low Carb Wellness Support';
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main>
        {/* HERO - Full Screen Dark with Gradient */}
        <section className="relative min-h-screen flex items-center overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src={siteImages.heroMain}
              alt="Fitness lifestyle"
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50" />
          </div>
          
          {/* Animated Gradient Orbs */}
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-vybe-600/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-cyan-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
            <div className="max-w-2xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-vybe-600/20 border border-vybe-500/30 rounded-full text-vybe-400 text-sm font-medium mb-8 backdrop-blur-sm">
                <Zap className="w-4 h-4" />
                Premium Keto Supplements
              </div>
              
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white mb-6 leading-tight">
                Unlock Your
                <span className="block bg-gradient-to-r from-vybe-400 via-cyan-400 to-coral-400 bg-clip-text text-transparent">
                  Full Potential
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-xl">
                Scientifically formulated keto supplements to fuel your body, sharpen your mind, and elevate your performance.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/shop"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full transition-all shadow-button hover:shadow-glow-purple"
                >
                  Shop Collection
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full transition-all border border-white/20 hover:bg-white/20"
                >
                  <Play className="w-5 h-5" />
                  Learn More
                </Link>
              </div>
              
              {/* Stats */}
              <div className="flex gap-10 mt-14 pt-10 border-t border-white/10">
                {[
                  { value: '12+', label: 'Products' },
                  { value: '6', label: 'Categories' },
                  { value: '99$', label: 'Free Ship' },
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="text-3xl font-bold text-white">{stat.value}</div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
              <div className="w-1 h-3 bg-white/50 rounded-full animate-bounce" />
            </div>
          </div>
        </section>

        {/* FEATURED PRODUCTS - Cards Grid */}
        <section className="py-24 bg-slate-900 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-vybe-500/50 to-transparent" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14">
              <div>
                <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Best Sellers</span>
                <h2 className="font-display text-4xl lg:text-5xl text-white">
                  Popular Products
                </h2>
              </div>
              <Link 
                to="/shop"
                className="hidden lg:inline-flex items-center gap-2 text-slate-400 hover:text-vybe-400 transition-colors font-medium mt-6 lg:mt-0"
              >
                View All Products
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product, i) => (
                <Link 
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className="group relative bg-slate-800/50 rounded-3xl overflow-hidden border border-slate-700/50 hover:border-vybe-500/50 transition-all hover:shadow-card-hover"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="aspect-square bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {product.badge && (
                      <span className="absolute top-4 left-4 px-3 py-1.5 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white text-xs font-bold rounded-full shadow-lg">
                        {product.badge}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-5">
                    <p className="text-vybe-400 text-xs font-semibold uppercase tracking-wider mb-2">
                      {categories.find(c => c.id === product.categoryId)?.name}
                    </p>
                    <h3 className="font-semibold text-white mb-3 group-hover:text-vybe-400 transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-white">
                        ${product.price.toFixed(2)}
                      </span>
                      <span className="px-4 py-2 bg-vybe-600/20 text-vybe-400 rounded-full text-sm font-semibold group-hover:bg-vybe-600 group-hover:text-white transition-all">
                        View
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-10 lg:hidden">
              <Link 
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 bg-vybe-600 text-white font-bold rounded-full"
              >
                View All Products
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* CATEGORIES - Horizontal Scroll */}
        <section className="py-24 bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-cyan-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Browse</span>
              <h2 className="font-display text-4xl lg:text-5xl text-white mb-4">
                Shop by Category
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Find exactly what you need for your keto journey
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-800"
                >
                  <img 
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <span className="text-xs text-vybe-400 font-medium">{category.productCount} Products</span>
                    <h3 className="text-white font-bold text-lg group-hover:text-vybe-400 transition-colors">
                      {category.name}
                    </h3>
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* BENEFITS - Dark Cards */}
        <section className="py-24 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-coral-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Why Keto Vybe</span>
              <h2 className="font-display text-4xl lg:text-5xl text-white mb-4">
                The Vybe Difference
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Flame, title: 'Ketosis Support', desc: 'Formulas to help maintain ketosis', color: 'from-coral-600 to-coral-500' },
                { icon: Zap, title: 'Clean Energy', desc: 'Sustained power without the crash', color: 'from-cyan-600 to-cyan-500' },
                { icon: Target, title: 'Focused Mind', desc: 'Mental clarity for peak performance', color: 'from-vybe-600 to-vybe-500' },
                { icon: Heart, title: 'Total Wellness', desc: 'Support for your whole body', color: 'from-coral-600 to-vybe-500' },
              ].map((item, i) => (
                <div key={i} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-vybe-500/30 transition-all group">
                  <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SPLIT SECTION - Image + Content */}
        <section className="py-24 bg-slate-950 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Image */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-3xl overflow-hidden">
                  <img 
                    src={siteImages.lifestyleSection}
                    alt="Active lifestyle"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-vybe-600 to-cyan-500 rounded-2xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-black text-white">99+</div>
                    <div className="text-xs text-white/80">Free Ship</div>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div>
                <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-4 block">Fuel Your Goals</span>
                <h2 className="font-display text-4xl text-white mb-6">
                  Supplements Built for the Keto Lifestyle
                </h2>
                <p className="text-slate-400 leading-relaxed mb-8">
                  Whether you're just starting keto or you're a seasoned pro, our supplements are designed to support your unique needs. From energy and focus to sleep and recovery.
                </p>
                <ul className="space-y-4 mb-8">
                  {[
                    'Premium quality ingredients',
                    'Third-party tested for purity',
                    'Made in USA facilities',
                    'Fast, free shipping on $99+',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="w-6 h-6 bg-vybe-600/20 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-vybe-400" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/shop"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full hover:shadow-glow-purple transition-all"
                >
                  Explore Products
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BADGES */}
        <section className="py-16 bg-slate-900 border-t border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: Truck, title: 'Free Shipping', desc: 'On orders $99+' },
                { icon: Shield, title: 'Secure Checkout', desc: '256-bit encryption' },
                { icon: Award, title: 'Quality Tested', desc: 'Third-party verified' },
                { icon: Zap, title: 'Fast Delivery', desc: '3-5 business days' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-6 h-6 text-vybe-400" />
                  </div>
                  <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                  <p className="text-slate-500 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-24 bg-slate-950 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-mesh opacity-30" />
          
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-vybe-600 to-coral-500 rounded-2xl mb-8">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-4xl lg:text-5xl text-white mb-6">
              Start Your Transformation
            </h2>
            <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">
              Join thousands who trust Keto Vybe for their supplement needs. Quality you can feel, results you can see.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/shop"
                className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold text-lg rounded-full hover:shadow-glow-purple transition-all"
              >
                Shop Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white/10 text-white font-semibold text-lg rounded-full border border-white/20 hover:bg-white/20 transition-all"
              >
                Questions?
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default HomePage;
